// Curated Video & Reading Resources for Data Science for Finance
// Organized by stage — high-quality, free, from reputable sources

export const STAGE_RESOURCES = {
  1: {
    stageName: 'Python Foundations for Finance',
    videos: [
      {
        id: 'rfscVS0vtbw',
        title: 'Learn Python — Full Course for Beginners',
        creator: 'freeCodeCamp',
        duration: '4h 26m',
        description: 'Complete Python tutorial from zero. Covers variables, data types, loops, functions, and more — everything you need for Stage 1.',
        tags: ['Beginner', 'Complete Course']
      },
      {
        id: 'YYXdXT2l-Gg',
        title: 'Python Variable & Data Types — Crash Course',
        creator: 'Corey Schafer',
        duration: '11m',
        description: 'Focused dive into Python variables, strings, integers, floats, and type conversion. Part of Corey Schafer\'s legendary Python series.',
        tags: ['Variables', 'Data Types']
      },
      {
        id: 'HGOBQPFzWKo',
        title: 'Automate the Boring Stuff with Python (Ch 1-6)',
        creator: 'Al Sweigart',
        duration: '3h 47m',
        description: 'Learn practical Python automation: flow control, functions, lists, dictionaries. Perfect for building your first financial calculators.',
        tags: ['Practical', 'Automation']
      }
    ],
    readings: [
      {
        title: 'Python for Everybody — Full Course Lessons',
        url: 'https://www.py4e.com/lessons',
        source: 'Dr. Charles Severance (UMich)',
        type: 'course',
        description: 'Free textbook and video lectures from the world\'s most popular Python course. Start here if you prefer structured learning.'
      },
      {
        title: 'Python Variables — A Complete Guide',
        url: 'https://realpython.com/python-variables/',
        source: 'Real Python',
        type: 'tutorial',
        description: 'In-depth guide to variables, naming conventions, and how Python handles memory. Real Python is the gold standard for Python tutorials.'
      },
      {
        title: 'Automate the Boring Stuff with Python',
        url: 'https://automatetheboringstuff.com/',
        source: 'Al Sweigart',
        type: 'book',
        description: 'Free online book. The best practical introduction to Python — you\'ll build real programs that save you hours of work.'
      },
      {
        title: 'Python Official Tutorial',
        url: 'https://docs.python.org/3/tutorial/index.html',
        source: 'Python.org',
        type: 'docs',
        description: 'The official tutorial from Python\'s creators. A bit dry, but authoritative and comprehensive.'
      },
      {
        title: 'NumPy Financial Library',
        url: 'https://numpy.org/numpy-financial/',
        source: 'NumPy',
        type: 'library',
        description: 'Financial functions for Python: NPV, IRR, PMT, FV, PV. Essential for time-value-of-money calculations.'
      }
    ]
  },

  2: {
    stageName: 'Data Manipulation with Pandas',
    videos: [
      {
        id: '2uvysYbKdjM',
        title: 'Complete Python Pandas Tutorial (2025 Edition)',
        creator: 'Keith Galli',
        duration: '1h 40m',
        description: 'Hands-on Pandas tutorial covering DataFrames, filtering, groupby, merging, and real-world data analysis. Updated for 2025.',
        tags: ['Complete Tutorial', 'Pandas 2.0']
      },
      {
        id: 'Mgn8ALGwrlM',
        title: '3 Examples of Pandas for Financial Market Data',
        creator: 'PyQuant News',
        duration: '15m',
        description: 'Demonstrates Pandas for real financial analysis: time series manipulation, merging datasets, and complex financial calculations.',
        tags: ['Finance', 'Time Series']
      },
      {
        id: 'b9RgHa1CnH4',
        title: 'Quant Finance with Python and Pandas — Getting Started',
        creator: 'QuantPy',
        duration: '25m',
        description: 'First in a series on using Pandas for quant finance: fetching stock data with yfinance, calculating returns, and Sharpe ratios.',
        tags: ['Quant Finance', 'yfinance']
      }
    ],
    readings: [
      {
        title: '10 Minutes to Pandas',
        url: 'https://pandas.pydata.org/docs/user_guide/10min.html',
        source: 'Pandas Official Docs',
        type: 'tutorial',
        description: 'The official quick-start guide. In 10 minutes you\'ll understand DataFrames, indexing, and basic operations.'
      },
      {
        title: 'Pandas User Guide — Indexing & Selecting',
        url: 'https://pandas.pydata.org/docs/user_guide/indexing.html',
        source: 'Pandas Official Docs',
        type: 'docs',
        description: 'Deep dive into .loc, .iloc, boolean indexing — the operations you\'ll use every day in financial analysis.'
      },
      {
        title: 'Python for Finance — Yves Hilpisch',
        url: 'https://home.tpq.io/books/py4fi/',
        source: 'The Python Quants',
        type: 'book',
        description: 'The definitive book on Python for quantitative finance. Written by the creator of The Python Quants platform.'
      },
      {
        title: 'Pandas Time Series Guide',
        url: 'https://pandas.pydata.org/docs/user_guide/timeseries.html',
        source: 'Pandas Official Docs',
        type: 'docs',
        description: 'Master DatetimeIndex, resampling, and rolling windows — the backbone of every financial time series analysis.'
      },
      {
        title: 'yfinance — Free Market Data for Python',
        url: 'https://pypi.org/project/yfinance/',
        source: 'PyPI',
        type: 'library',
        description: 'Download decades of stock data for free. The most popular library for fetching Yahoo Finance data.'
      }
    ]
  },

  3: {
    stageName: 'Statistics & Financial Mathematics',
    videos: [
      {
        id: 'XQoLVl31ZfQ',
        title: 'Bayes\' Theorem — The Simplest Case',
        creator: '3Blue1Brown',
        duration: '15m',
        description: 'Grant Sanderson\'s beautifully animated explanation of Bayes\' theorem. Changes how you think about probability and updating beliefs.',
        tags: ['Probability', 'Visual Math']
      },
      {
        id: 'uzkc-qNVoOk',
        title: 'Basic Probability — Foundations',
        creator: 'Khan Academy',
        duration: '10m',
        description: 'Sal Khan teaches probability from scratch. Part of Khan Academy\'s legendary statistics course used in classrooms worldwide.',
        tags: ['Probability', 'Foundations']
      },
      {
        id: 'xxpc-HPKN28',
        title: 'StatQuest: Statistics Fundamentals Playlist',
        creator: 'StatQuest (Josh Starmer)',
        duration: '12m',
        description: 'Josh Starmer explains histograms, mean, standard deviation, and distributions with clarity that no textbook matches. BAM!',
        tags: ['Statistics', 'Beginner-Friendly']
      },
      {
        id: 'ofzrvCPRRjw',
        title: 'Introduction to Quantitative Finance in Python',
        creator: 'Jonathan Hofmann',
        duration: '2h',
        description: 'Covers ARIMA, portfolio optimization with Riskfolio, VaR, backtesting — connects statistics directly to finance applications.',
        tags: ['Quant Finance', 'Risk Metrics']
      }
    ],
    readings: [
      {
        title: 'Khan Academy — Statistics & Probability',
        url: 'https://www.khanacademy.org/math/statistics-probability',
        source: 'Khan Academy',
        type: 'course',
        description: 'Free, self-paced course covering descriptive statistics, probability, distributions, regression, and inference. The gold standard.'
      },
      {
        title: 'MIT OCW — Finance Theory I',
        url: 'https://ocw.mit.edu/courses/15-401-finance-theory-i-fall-2008/',
        source: 'MIT OpenCourseWare',
        type: 'course',
        description: 'Andrew Lo\'s legendary finance course. Covers time value of money, portfolio theory, CAPM — free from MIT.'
      },
      {
        title: 'SciPy Statistics Reference',
        url: 'https://docs.scipy.org/doc/scipy/reference/stats.html',
        source: 'SciPy',
        type: 'docs',
        description: 'The complete reference for statistical functions in Python: distributions, tests, correlation, regression.'
      },
      {
        title: 'Quantitative Risk Management (Princeton)',
        url: 'https://press.princeton.edu/books/hardcover/9780691166278/quantitative-risk-management',
        source: 'Princeton University Press',
        type: 'book',
        description: 'The definitive textbook on VaR, CVaR, and tail risk. Used in risk management programs at every major bank.'
      }
    ]
  },

  4: {
    stageName: 'Data Visualization for Finance',
    videos: [
      {
        id: 'B7zdC5FaxWY',
        title: 'Mastering Data Visualization — Matplotlib, Seaborn & Plotly',
        creator: 'Analytics Vidhya',
        duration: '13m',
        description: 'Comprehensive guide: Matplotlib for basics, Seaborn for statistical charts, Plotly for interactive dashboards. All in one video.',
        tags: ['Matplotlib', 'Plotly', 'Seaborn']
      },
      {
        id: 'Q6w0SOgICPs',
        title: 'Plotly Python Tutorial — Interactive Charts',
        creator: 'Charming Data',
        duration: '20m',
        description: 'Build interactive financial charts with Plotly: line charts, candlesticks, heatmaps. The library used by Bloomberg-style dashboards.',
        tags: ['Plotly', 'Interactive']
      },
      {
        id: 'mHbw3o4-Orc',
        title: 'The ONLY 7 Charts You Need as a Finance Pro',
        creator: 'Kenji Explains',
        duration: '18m',
        description: 'Which chart for which question? Bar, line, scatter, waterfall, bullet — chart selection framework used by Wall Street analysts.',
        tags: ['Chart Selection', 'Finance']
      }
    ],
    readings: [
      {
        title: 'Matplotlib Official Tutorials',
        url: 'https://matplotlib.org/stable/tutorials/index.html',
        source: 'Matplotlib',
        type: 'docs',
        description: 'Official tutorials from the foundation of all Python charting. Start with "Pyplot tutorial" and "Lifecycle of a Plot."'
      },
      {
        title: 'Plotly Python Documentation',
        url: 'https://plotly.com/python/',
        source: 'Plotly',
        type: 'docs',
        description: 'Interactive charts for finance: candlesticks, OHLC, heatmaps, subplots. The most used library for financial dashboards.'
      },
      {
        title: 'Storytelling with Data — Cole Nussbaumer Knaflic',
        url: 'https://www.storytellingwithdata.com/',
        source: 'storytellingwithdata.com',
        type: 'book',
        description: 'The bible of data visualization design. Teaches you to communicate insights, not just show data. Used at Google, Bloomberg, McKinsey.'
      },
      {
        title: 'Streamlit — Build Data Apps Fast',
        url: 'https://streamlit.io/',
        source: 'Streamlit',
        type: 'tool',
        description: 'Turn Python scripts into shareable web apps in minutes. Perfect for deploying your financial dashboards.'
      }
    ]
  },

  5: {
    stageName: 'Financial Modeling in Python',
    videos: [
      {
        id: 'SKTxEvqFrdg',
        title: 'DCF Valuation in Python — Full Course',
        creator: 'Financial Modeling',
        duration: '45m',
        description: 'Build a complete Discounted Cash Flow model in Python. Includes Monte Carlo simulation for fair value distributions.',
        tags: ['DCF', 'Valuation']
      },
      {
        id: 'drswh8D84sE',
        title: 'Monte Carlo Simulation in Python for Finance',
        creator: 'Ryan O\'Connell',
        duration: '8m',
        description: 'Hands-on Monte Carlo: simulate portfolio returns, model uncertainty, and estimate retirement outcomes using NumPy.',
        tags: ['Monte Carlo', 'Portfolio']
      },
      {
        id: 'R67XuYc9NQ4',
        title: 'Exploring Economic Data with Python (FRED API)',
        creator: 'Rob Mulla',
        duration: '28m',
        description: 'Pull real economic data from FRED, clean it with Pandas, join datasets, and explore macroeconomic indicators visually.',
        tags: ['FRED', 'Economic Data']
      }
    ],
    readings: [
      {
        title: 'Corporate Finance Institute — FMVA',
        url: 'https://corporatefinanceinstitute.com/',
        source: 'CFI',
        type: 'course',
        description: 'Industry-standard financial modeling certification. Free introductory courses on DCF, 3-statement models, and valuation.'
      },
      {
        title: 'Statsmodels Documentation',
        url: 'https://www.statsmodels.org/',
        source: 'Statsmodels',
        type: 'library',
        description: 'Python library for statistical modeling: OLS regression, time series analysis (ARIMA), hypothesis testing. The Python equivalent of R.'
      },
      {
        title: 'FRED Economic Data',
        url: 'https://fred.stlouisfed.org/',
        source: 'Federal Reserve Bank of St. Louis',
        type: 'data',
        description: 'Free access to 800,000+ economic time series: GDP, CPI, interest rates, unemployment. The most important data source in macro finance.'
      },
      {
        title: 'Options, Futures and Other Derivatives — John Hull',
        url: 'https://www.pearson.com/en-us/subject-catalog/p/options-futures-and-other-derivatives/P200000005938',
        source: 'Pearson',
        type: 'book',
        description: 'The definitive derivatives textbook. Every options trader and risk manager has this on their desk.'
      }
    ]
  },

  6: {
    stageName: 'Machine Learning for Finance',
    videos: [
      {
        id: '9Y3yaoi9rUQ',
        title: 'Machine Learning & Quant Strategies — Full Course',
        creator: 'freeCodeCamp',
        duration: '3h',
        description: 'Build ML trading strategies: unsupervised learning on S&P 500, sentiment analysis for NASDAQ, and GARCH models. Complete with Python code.',
        tags: ['ML', 'Trading Strategies']
      },
      {
        id: 'gyE3bYPsvu8',
        title: 'Build a Machine Learning Trading Indicator in Python',
        creator: 'CodeTrading',
        duration: '18m',
        description: 'Step-by-step: build an ML classifier that predicts market trends using 20+ technical indicators. Includes evaluation with ROC curves.',
        tags: ['Classification', 'Indicators']
      },
      {
        id: 'Gv9_4yMHFhI',
        title: 'Scikit-Learn Crash Course — ML in Python',
        creator: 'freeCodeCamp',
        duration: '2h 28m',
        description: 'Complete scikit-learn tutorial: classification, regression, cross-validation, pipelines. The ML library you\'ll use most in finance.',
        tags: ['Scikit-Learn', 'Complete']
      }
    ],
    readings: [
      {
        title: 'Scikit-Learn Documentation',
        url: 'https://scikit-learn.org/stable/user_guide.html',
        source: 'Scikit-Learn',
        type: 'docs',
        description: 'The most-used ML library in Python. Excellent docs with tutorials on classification, regression, clustering, and model selection.'
      },
      {
        title: 'Advances in Financial Machine Learning — de Prado',
        url: 'https://www.amazon.com/Advances-Financial-Machine-Learning-Marcos/dp/1119482089',
        source: 'Marcos Lopez de Prado',
        type: 'book',
        description: 'The book that changed quant finance. Exposes why most backtested strategies fail and introduces proper ML validation for finance.'
      },
      {
        title: 'Machine Learning for Trading — Georgia Tech (Free)',
        url: 'https://www.udacity.com/course/machine-learning-for-trading--ud501',
        source: 'Udacity / Georgia Tech',
        type: 'course',
        description: 'Free Georgia Tech course on ML for algorithmic trading. Covers learners, regression, KNN, and portfolio optimization.'
      },
      {
        title: 'Feature Engineering for ML — Alice Zheng',
        url: 'https://www.oreilly.com/library/view/feature-engineering-for/9781491953235/',
        source: 'O\'Reilly',
        type: 'book',
        description: 'The art of creating good features from raw data. In finance, feature engineering separates profitable models from noise.'
      }
    ]
  },

  7: {
    stageName: 'Macro Factor Modeling & Strategy',
    videos: [
      {
        id: 'PHe0bXAIuk0',
        title: 'How the Economic Machine Works',
        creator: 'Ray Dalio / Principles',
        duration: '31m',
        description: 'Ray Dalio explains the entire economy in 30 minutes: transactions, credit cycles, debt, inflation. The most-watched economics video on YouTube (250M+ views).',
        tags: ['Macro', 'Essential Viewing']
      },
      {
        id: 'cdwGyCLn770',
        title: 'Stock Market Modeling with Python — Quant Finance',
        creator: 'QuantPy',
        duration: '20m',
        description: 'Build stock market models with pandas and numpy: returns analysis, factor exposures, and systematic signals.',
        tags: ['Factor Models', 'Python']
      },
      {
        id: '2ODvr8Q9nDI',
        title: 'Macro Trading Strategies Explained',
        creator: 'Patrick Boyle',
        duration: '22m',
        description: 'Former hedge fund manager Patrick Boyle explains how macro strategies work: yield curve trades, carry, momentum, and regime detection.',
        tags: ['Macro Strategy', 'Hedge Funds']
      }
    ],
    readings: [
      {
        title: 'How the Economic Machine Works — Full Essay',
        url: 'https://economicprinciples.org/how-the-economic-machine-works',
        source: 'Bridgewater / Ray Dalio',
        type: 'article',
        description: 'The full written version of Dalio\'s famous video. Essential reading for understanding economic cycles and macro investing.'
      },
      {
        title: 'Principles — Ray Dalio',
        url: 'https://www.principles.com/',
        source: 'Ray Dalio',
        type: 'book',
        description: 'Dalio\'s framework for decision-making, investing, and life. The philosophy behind Bridgewater\'s $150B+ in AUM.'
      },
      {
        title: 'FRED Economic Data',
        url: 'https://fred.stlouisfed.org/',
        source: 'Federal Reserve Bank of St. Louis',
        type: 'data',
        description: '800,000+ economic time series. GDP, CPI, yield curves, unemployment — the primary data source for macro analysis.'
      },
      {
        title: 'Bridgewater Research & Insights',
        url: 'https://www.bridgewater.com/research-and-insights',
        source: 'Bridgewater Associates',
        type: 'article',
        description: 'Public research from the world\'s largest hedge fund. Their papers on risk parity, economic regimes, and diversification are foundational.'
      }
    ]
  },

  8: {
    stageName: 'Risk Parity, All-Weather & Portfolio Construction',
    videos: [
      {
        id: 'zriRtS3W7YM',
        title: 'Risk Parity Portfolio Explained',
        creator: 'Julia',
        duration: '12m',
        description: 'Clear explanation of risk parity: why equal-risk allocation outperforms equal-weight. The foundation of Bridgewater\'s All Weather strategy.',
        tags: ['Risk Parity', 'All Weather']
      },
      {
        id: 'ofzrvCPRRjw',
        title: 'Quantitative Finance in Python — Portfolio Optimization',
        creator: 'Jonathan Hofmann',
        duration: '2h',
        description: 'Full tutorial on portfolio optimization with Python: mean-variance, risk parity, efficient frontier. Includes Riskfolio-Lib implementation.',
        tags: ['Optimization', 'Riskfolio']
      },
      {
        id: '9Y3yaoi9rUQ',
        title: 'Algorithmic Trading & Portfolio Optimization',
        creator: 'freeCodeCamp',
        duration: '3h',
        description: 'Build systematic strategies end-to-end: data collection, signal generation, portfolio construction, and backtesting with Python.',
        tags: ['Systematic', 'Backtesting']
      }
    ],
    readings: [
      {
        title: 'The All Weather Story — Bridgewater',
        url: 'https://www.bridgewater.com/research-and-insights/the-all-weather-story',
        source: 'Bridgewater Associates',
        type: 'article',
        description: 'The origin story of the All Weather portfolio. How Dalio designed a portfolio to perform in any economic environment.'
      },
      {
        title: 'Systematic Trading — Robert Carver',
        url: 'https://www.amazon.com/Systematic-Trading-designing-trading-investing/dp/0857194453',
        source: 'Robert Carver',
        type: 'book',
        description: 'The practical guide to building systematic trading and investing systems. Written by a former AHL (Man Group) portfolio manager.'
      },
      {
        title: 'Active Portfolio Management — Grinold & Kahn',
        url: 'https://www.amazon.com/Active-Portfolio-Management-Quantitative-Controlling/dp/0070248826',
        source: 'Grinold & Kahn',
        type: 'book',
        description: 'The textbook on quantitative portfolio management. Covers the fundamental law of active management, information ratios, and alpha research.'
      },
      {
        title: 'Riskfolio-Lib — Portfolio Optimization in Python',
        url: 'https://riskfolio-lib.readthedocs.io/',
        source: 'Riskfolio-Lib',
        type: 'library',
        description: 'Python library for portfolio optimization: mean-variance, risk parity, hierarchical risk parity, CVaR optimization. The modern quant\'s toolkit.'
      }
    ]
  }
};
